import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ImagePlus, Link as LinkIcon, Trash2, Upload, Image as ImageIcon, Loader2, AlertCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import {
  getAllImages,
  uploadImage,
  deleteImage,
} from "../../services/imageService";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------
function DeleteDialog({ imageId, imageLabel, onDelete }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteImage(imageId);
      onDelete(imageId);
      toast.success("Gambar berhasil dihapus");
    } catch {
      toast.error("Gagal menghapus gambar");
    } finally {
      setDeleting(false);
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#fdfbf7]">
            Hapus Gambar
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#a6a196]">
            Apakah Anda yakin ingin menghapus{" "}
            <span className="text-[#fdfbf7] font-medium">{imageLabel}</span>?
            Tindakan ini tidak dapat dibatalkan.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-[#d4af37]/20 text-[#a6a196] hover:bg-[#d4af37]/10 hover:text-[#fdfbf7]">
            Batal
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-500/80 hover:bg-red-500 text-white border-0"
          >
            {deleting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Menghapus...
              </>
            ) : (
              "Hapus"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Full image preview (lightbox)
// ---------------------------------------------------------------------------
function ImagePreview({ image, onClose }) {
  if (!image) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-[#0a0908]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        onClick={onClose}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 w-12 h-12 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all z-10"
          aria-label="Tutup"
        >
          <X size={20} />
        </button>

        <motion.div
          key={image.id}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.3 }}
          className="relative max-w-5xl w-full max-h-[85vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={image.content}
            alt=""
            className="max-h-[85vh] max-w-full w-auto h-auto object-contain drop-shadow-2xl"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Upload section
// ---------------------------------------------------------------------------
function UploadSection({ onUploaded }) {
  const [mode, setMode] = useState("file"); // "file" | "url"
  const [files, setFiles] = useState([]);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const canSubmit = mode === "file" ? files.length > 0 : url.trim().length > 0;

  const handleUpload = async () => {
    if (!canSubmit) return;
    setUploading(true);
    try {
      if (mode === "file") {
        const results = await Promise.all(files.map((f) => uploadImage({ file: f })));
        results.forEach((r) => onUploaded(r));
        toast.success(`${files.length} gambar berhasil diunggah`);
        setFiles([]);
      } else {
        const result = await uploadImage({ url: url.trim() });
        toast.success("URL gambar berhasil disimpan");
        onUploaded(result);
        setUrl("");
      }
      const input = document.getElementById("image-file-input");
      if (input) input.value = "";
    } catch (err) {
      toast.error(err?.message || "Gagal mengunggah");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-[#0a0908] border-[#d4af37]/20">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-[#fdfbf7] flex items-center gap-2">
          <Upload className="w-4 h-4 text-[#d4af37]" />
          Unggah Gambar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode toggle */}
        <div className="flex items-center gap-2 p-1 bg-[#15120f] rounded-lg w-fit">
          <button
            onClick={() => setMode("file")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              mode === "file"
                ? "bg-[#d4af37]/15 text-[#d4af37]"
                : "text-[#a6a196] hover:text-[#fdfbf7]"
            }`}
          >
            <ImagePlus className="w-3.5 h-3.5" />
            File
          </button>
          <button
            onClick={() => setMode("url")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              mode === "url"
                ? "bg-[#d4af37]/15 text-[#d4af37]"
                : "text-[#a6a196] hover:text-[#fdfbf7]"
            }`}
          >
            <LinkIcon className="w-3.5 h-3.5" />
            URL
          </button>
        </div>

        {/* File upload */}
        {mode === "file" && (
          <div className="relative">
            <Input
              id="image-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="bg-[#15120f] border-[#d4af37]/15 text-[#fdfbf7] file:bg-[#d4af37]/10 file:text-[#d4af37] file:border-0 file:rounded-md file:px-3 file:py-1 file:text-xs file:font-medium hover:file:bg-[#d4af37]/20 cursor-pointer"
            />
            <p className="text-[10px] text-[#a6a196] mt-1">Anda bisa memilih beberapa file sekaligus</p>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-[#a6a196] bg-[#0a0908] rounded px-3 py-1.5">
                    <span className="truncate flex-1">{f.name}</span>
                    <span className="shrink-0 ml-2">{(f.size / 1024).toFixed(1)} KB</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* URL input */}
        {mode === "url" && (
          <Input
            type="url"
            placeholder="https://example.com/image.jpg"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="bg-[#15120f] border-[#d4af37]/15 text-[#fdfbf7] placeholder:text-[#a6a196]/60"
          />
        )}

        <Button
          onClick={handleUpload}
          disabled={!canSubmit || uploading}
          className="bg-[#d4af37] hover:bg-[#c49f2e] text-[#0a0908] font-medium text-sm"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Mengunggah...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {mode === "file"
                ? files.length > 1
                  ? `Unggah ${files.length} File`
                  : "Unggah File"
                : "Simpan URL"}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Image card
// ---------------------------------------------------------------------------
function ImageCard({ image, onDeleted, onPreview }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const isBase64 = image.type === "base64";
  const typeLabel = isBase64 ? "Internal" : image.type;
  const src = image.content;

  return (
    <div className="group relative bg-[#15120f] rounded-lg border border-[#d4af37]/10 overflow-hidden hover:border-[#d4af37]/30 transition-all duration-300">
      {/* Thumbnail */}
      <div className="aspect-[4/3] bg-[#0a0908] relative overflow-hidden">
        {!loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-[#d4af37]/40 animate-spin" />
          </div>
        )}
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#a6a196] gap-1">
            <AlertCircle className="w-6 h-6" />
            <span className="text-xs">Failed to load</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onPreview?.(image)}
            className="w-full h-full text-left"
            aria-label="Lihat gambar"
          >
            <img
              src={src}
              alt=""
              onLoad={() => setLoaded(true)}
              onError={() => setError(true)}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                loaded ? "opacity-100" : "opacity-0"
              }`}
            />
          </button>
        )}
      </div>

      {/* Delete button */}
      <DeleteDialog
        imageId={image.id}
        imageLabel={`Image ${image.id?.slice(0, 8)}`}
        onDelete={onDeleted}
      />

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2">
        <Badge
          variant="outline"
          className={`text-[10px] uppercase tracking-wider font-medium border ${
            isBase64
              ? "border-[#d4af37]/20 text-[#d4af37] bg-[#d4af37]/5"
              : "border-blue-500/20 text-blue-400 bg-blue-500/5"
          }`}
        >
          {typeLabel}
        </Badge>
        <span className="text-[10px] text-[#a6a196] font-mono truncate ml-2 max-w-[120px]">
          {image.id?.slice(0, 12)}...
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function EmptyState() {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-[#d4af37]/5 border border-[#d4af37]/10 flex items-center justify-center mb-4">
        <ImageIcon className="w-7 h-7 text-[#d4af37]/40" />
      </div>
      <h3 className="text-lg font-medium text-[#fdfbf7] mb-1">
        Belum ada gambar
      </h3>
      <p className="text-sm text-[#a6a196] max-w-xs">
        Unggah gambar atau simpan URL gambar di atas untuk memulai.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image Library page
// ---------------------------------------------------------------------------
export default function ImageLibrary() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllImages();
      setImages(data);
    } catch {
      toast.error("Gagal memuat gambar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUploaded = (newImage) => {
    setImages((prev) => [newImage, ...prev]);
  };

  const handleDeleted = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
          <h1 className="text-2xl font-display text-[#fdfbf7]">
            Image Library
          </h1>
        </div>
        <p className="text-sm text-[#a6a196] ml-4">
           Kelola gambar yang digunakan di website. Unggah gambar baru atau simpan URL
           eksternal.
        </p>
      </div>

      {/* Upload section */}
      <UploadSection onUploaded={handleUploaded} />

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-[#15120f] rounded-lg border border-[#d4af37]/10 overflow-hidden animate-pulse"
            >
              <div className="aspect-[4/3] bg-[#0a0908]" />
              <div className="h-10 bg-[#15120f]" />
            </div>
          ))}
        </div>
      )}

      {/* Image grid */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.length === 0 ? (
            <EmptyState />
          ) : (
            images.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                onDeleted={handleDeleted}
                onPreview={(img) => setPreviewImage(img)}
              />
            ))
          )}
        </div>
      )}

      {/* Count */}
      {!loading && images.length > 0 && (
        <p className="text-xs text-[#a6a196]/60 text-center">
          {images.length} gambar total
        </p>
      )}

      {/* Full image preview */}
      <ImagePreview
        image={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
}
